/*
  Effets au défilement — C'est bon la vie

  Noms des effets (ceux de la banque d'effets ClickUp) :
    1. Lettres qui se déplient       — fold / unfold + blur reveal : chaque lettre,
                                       articulée par le haut, se déplie vers le
                                       visiteur en sortant du flou (inspiré du
                                       FoldText de React Bits, sans GSAP).
                                       Titres de section (pas celui de la bannière).
    2. Apparition au défilement      — scroll-triggered reveal (surtitres, textes).
    3. Trait qui se dessine          — line draw (trait des surtitres, soulignement
                                       des liens « Voir tout… »).
    4. Cascade                       — staggered reveal (cartes de produits).
    5. Rideau                        — clip-path reveal (petites photos superposées).
    6. Zoom arrière au défilement    — scroll-linked zoom out (grandes photos).
    7. Bannière qui s'en va          — scroll-linked hero fade (texte de la bannière).
    8. Fondu enchaîné entre sections — sliding cross-fade : deux dégradés
                                       complémentaires de part et d'autre de chaque
                                       frontière ; le point de fondu glisse de la
                                       section du bas vers celle du haut, en
                                       accélérant. La frontière ne se voit jamais.
    9. Texte qui s'efface en montant — scroll-linked fade out : le bloc de texte
                                       d'une section devient transparent en
                                       approchant du haut de l'écran.

  Les effets 1 à 5 se rejouent : quand un élément ressort par le bas de l'écran
  (on remonte), il se réinitialise, et l'effet rejoue en redescendant.

  Règles qui évitent les bugs :
  - Rien n'est caché par le CSS seul. Sans ce script, tout reste visible ; le
    titre de la bannière ne l'est jamais (note de vitesse de Google).
  - Ce qui est déjà à l'écran au chargement est affiché tel quel, sans retard :
    la note de vitesse de Google ne bouge pas.
  - On anime `translate`, `scale` et la transparence : la carte graphique s'en
    charge seule. Ces propriétés s'ajoutent aux `transform` existants (zigzag des
    familles, carrousels) au lieu de les écraser.
  - « Réduire les animations » sur l'appareil : mode doux (fondus seulement, sans
    mouvement). Éditeur de thème : aucun effet.
  - Diagnostic à distance : ajouter ?cbl-diag=1 à l'adresse.
  - Les effets liés au défilement ne calculent que pour ce qui est proche de
    l'écran, une fois par image au plus.
*/
(() => {
  // iPhone : sans écouteur tactile, Safari n'applique pas :active, donc pas de
  // léger grossissement des cartes sous le doigt.
  document.addEventListener('touchstart', () => {}, { passive: true });

  const racineHtml = document.documentElement;

  // « Réduire les animations » (Réglages › Accessibilité › Mouvement sur iPhone,
  // Supprimer les animations sur Android). Avant, on coupait TOUT : un client
  // avec ce réglage voyait un site figé (cas de Larissa, sur ce site et sur
  // Futura). Les règles d'accessibilité demandent de réduire le MOUVEMENT, pas
  // de supprimer toute animation. Mode doux : on garde les fondus de couleur et
  // les apparitions en transparence ; pas de déplacement, de rotation, de zoom,
  // ni de flou (voir .cbl-calme dans cbl-effets.css).
  const calme = matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Diagnostic à distance : ajouter ?cbl-diag=1 à l'adresse. Un encadré dit ce
  // que l'appareil déclare, pour comprendre un « ça marche pas chez moi ».
  if (/[?&]cbl-diag/.test(location.search)) {
    const erreurs = [];
    addEventListener('error', (e) => erreurs.push(e.message));
    addEventListener('load', () => {
      const boite = document.createElement('pre');
      boite.style.cssText = 'position:fixed;left:8px;bottom:90px;z-index:2147483647;margin:0;padding:10px 12px;background:#1E1B19;color:#FAF7F2;font:12px/1.5 monospace;white-space:pre-wrap;max-width:calc(100vw - 16px);border-radius:6px;opacity:.94';
      boite.textContent = [
        'Effets C\'est bon la vie — diagnostic',
        `Réduire les animations : ${calme ? 'OUI → mode doux' : 'non'}`,
        `IntersectionObserver : ${'IntersectionObserver' in window ? 'oui' : 'NON'}`,
        `Éléments animés : ${document.querySelectorAll('.cbl-a-reveler').length}`,
        `Voiles entre sections : ${document.querySelectorAll('.cbl-voile').length}`,
        `Écran : ${innerWidth}×${innerHeight}, ${navigator.userAgent.match(/(iPhone OS|Android|Mac OS X|Windows NT) [\d_.]+/)?.[0] || '?'}`,
        `Erreurs : ${erreurs.length ? erreurs.join(' | ') : 'aucune'}`,
      ].join('\n');
      document.body.append(boite);
    });
  }

  if ((window.Shopify && Shopify.designMode) || !('IntersectionObserver' in window)) {
    racineHtml.classList.remove('cbl-fx');
    return;
  }
  if (calme) racineHtml.classList.add('cbl-calme');

  // Pas de :has() dans ces listes : un navigateur qui ne le connaît pas ferait
  // planter tout le script.
  const TITRES_LETTRES = '[class*="cbl-"][class*="__titre"], product-recommendations .text-block h1, product-recommendations .text-block h2, product-recommendations .text-block h3';
  const TEXTES = [
    '[class*="cbl-"][class*="__etiquette"]',
    '[class*="cbl-"][class*="__surtitre"]',
    '.cbl-familles__compteur',
  ].join(',');
  const LIENS = '.cbl-erable__lien, .cbl-rayon__lien, .cbl-vedettes__lien';
  const RIDEAUX = '.cbl-rayon__photo-sec, .cbl-erable__vignette';
  const CARTES = '.product-grid__item, product-recommendations .resource-list__item';
  const CARROUSELS = '[data-cbl-defile]';
  const ZOOMS = '[data-cbl-zoom]';
  const MENUS = '.menu-drawer__menu-item-text, .mega-menu__link, .menu-list__submenu .menu-list__link-title';

  // Ce qui défile. Sur téléphone et tablette, la page elle-même. À partir de
  // 990 px, Savor fait défiler un conteneur (`.page-wrapper`, base.css) et la
  // page ne bouge jamais : `scrollY` reste à 0 et l'événement `scroll` de la
  // fenêtre ne part pas. C'était le « saccadé » de l'ordinateur : les effets ne
  // se recalculaient qu'aux passages de seuils, par à-coups.
  const enveloppe = document.querySelector('.page-wrapper');
  const defileur = () =>
    enveloppe && /auto|scroll/.test(getComputedStyle(enveloppe).overflowY) ? enveloppe : null;
  const posY = () => (defileur() ? enveloppe.scrollTop : scrollY);
  const hautTotal = () => (defileur() ? enveloppe.scrollHeight : racineHtml.scrollHeight);

  // Hauteur d'écran stable : la barre d'adresse de Safari qui se replie ne doit
  // pas faire sauter les calculs.
  let hauteur = racineHtml.clientHeight;
  let largeur = innerWidth;
  const aLecran = (el) => {
    const r = el.getBoundingClientRect();
    return r.top < hauteur && r.bottom > 0;
  };

  /* ---------- Lettres : découpage ---------- */

  // Chaque mot devient un bloc insécable, chaque lettre un petit bloc animable.
  // Le texte intact reste lisible par les lecteurs d'écran (copie masquée).
  const decouper = (el) => {
    if (el.dataset.cblLettres) return;
    el.dataset.cblLettres = '1';
    const texte = el.textContent.replace(/\s+/g, ' ').trim();
    if (!texte) return;
    const marcheur = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    const noeuds = [];
    while (marcheur.nextNode()) noeuds.push(marcheur.currentNode);
    let i = 0;
    noeuds.forEach((n) => {
      if (!n.textContent.trim()) return;
      const lot = document.createDocumentFragment();
      n.textContent.split(/(\s+)/).forEach((morceau) => {
        if (!morceau) return;
        if (/^\s+$/.test(morceau)) return lot.append(' ');
        const mot = document.createElement('span');
        mot.className = 'cbl-mot';
        mot.setAttribute('aria-hidden', 'true');
        for (const c of morceau) {
          const l = document.createElement('span');
          l.className = 'cbl-l';
          l.textContent = c;
          l.style.setProperty('--i', i++);
          mot.append(l);
        }
        lot.append(mot);
      });
      n.replaceWith(lot);
    });
    const lu = document.createElement('span');
    lu.className = 'cbl-lecteur';
    lu.textContent = texte;
    el.append(lu);
  };

  /* ---------- Apparitions qui se rejouent ---------- */

  const cascade = (zone) => {
    const bord = zone.getBoundingClientRect();
    let rang = 0;
    zone.querySelectorAll(':scope :is([data-cbl-piste], ul) > *').forEach((carte) => {
      const r = carte.getBoundingClientRect();
      const visible = r.right > bord.left && r.left < bord.right;
      carte.style.setProperty('--cbl-delai', visible ? `${Math.min(rang++, 6) * 120}ms` : '0ms');
    });
  };

  // On révèle un peu après l'entrée dans l'écran (à 80 % de sa hauteur) : le
  // visiteur a le temps de voir l'effet au lieu d'arriver quand il est fini.
  // Les éléments qui arrivent ensemble se suivent : surtitre, titre, carte 1, 2…
  const reveler = (entrees) => {
    let rang = 0;
    entrees
      .filter((e) => e.isIntersecting && !e.target.classList.contains('cbl-revele'))
      .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top || a.boundingClientRect.left - b.boundingClientRect.left)
      .forEach((e) => {
        const el = e.target;
        const pas = el.classList.contains('cbl-carte') ? 120 : 160;
        el.style.setProperty('--cbl-delai', `${Math.min(rang++, 6) * pas}ms`);
        if (el.matches(CARROUSELS)) cascade(el);
        el.classList.add('cbl-revele');
      });
  };
  // Cartes, photos, liens : à 80 % de la hauteur de l'écran.
  const montrer = new IntersectionObserver(reveler, { rootMargin: '0px 0px -20% 0px' });
  // Titres et surtitres : à 70 %. Le fondu entre sections (plus rapide depuis
  // e6573c0) est alors passé : les lettres se déplient en pleine lumière, et le
  // titre arrive presque en même temps que son paragraphe.
  const montrerTitres = new IntersectionObserver(reveler, { rootMargin: '0px 0px -30% 0px' });

  // Ressorti par le bas (on remonte) : on réinitialise, sans transition, hors de
  // la vue. En redescendant, l'effet rejoue.
  const reinitialiser = new IntersectionObserver((entrees) => {
    entrees.forEach((e) => {
      if (!e.isIntersecting && e.boundingClientRect.top >= hauteur - 1) e.target.classList.remove('cbl-revele');
    });
  });

  const preparer = (el, classes, animerSiVisible = false) => {
    // L'intérieur d'un carrousel est recopié tel quel par cbl-defile.js : une copie
    // ne serait jamais surveillée. C'est la rangée entière qui est gérée.
    if (el.dataset.cblFx || (el.closest(CARROUSELS) && !el.matches(CARROUSELS))) return;
    el.dataset.cblFx = '1';
    // Déjà à l'écran : posé directement dans son état final, sans animation.
    const deja = !animerSiVisible && aLecran(el);
    el.classList.add('cbl-a-reveler', ...classes);
    if (deja) el.classList.add('cbl-revele');
    (classes.includes('cbl-lettres') || classes.includes('cbl-monte') ? montrerTitres : montrer).observe(el);
    reinitialiser.observe(el);
  };

  const balayer = (racine, chargement) => {
    racine.querySelectorAll(TITRES_LETTRES).forEach((el) => {
      if (el.closest('.cbl-hero')) return; // la bannière a son entrée à elle
      if (el.closest(CARROUSELS)) return;
      decouper(el);
      preparer(el, ['cbl-lettres']);
      suivreSortie(el.parentElement);
    });
    racine.querySelectorAll(TEXTES).forEach((el) => !el.closest('.cbl-hero') && preparer(el, ['cbl-monte']));
    // En-tête des suggestions (« Vous aimeriez aussi ») sans balise de titre
    racine.querySelectorAll('product-recommendations .text-block').forEach((el) => {
      if (!el.querySelector('h1, h2, h3')) preparer(el, ['cbl-monte']);
    });
    racine.querySelectorAll(LIENS).forEach((el) => preparer(el, ['cbl-trace']));
    racine.querySelectorAll(RIDEAUX).forEach((el) => preparer(el, ['cbl-rideau']));
    // Cartes ajoutées après coup (suggestions, page suivante) : toujours animées.
    racine.querySelectorAll(CARTES).forEach((el) => preparer(el, ['cbl-carte'], !chargement));
    racine.querySelectorAll(CARROUSELS).forEach((el) => preparer(el, ['cbl-rangee']));
    // Menus : plus d'animation des lettres (retirée le 2 oct., à la demande de
    // Mavrick) : un menu doit se lire tout de suite.
  };

  /* ---------- Bannière : entrée au chargement ---------- */

  const banniere = document.querySelector('.cbl-hero');
  const titreBanniere = banniere && banniere.querySelector('.cbl-hero__titre');
  const deuxImages = (fn) => requestAnimationFrame(() => requestAnimationFrame(fn));
  // Le titre de la bannière s'affiche tout de suite, sans lettres qui se
  // déplient (9 oct.) : c'est le premier texte que Google mesure, et le
  // cacher le temps de l'effet coûtait des points à PageSpeed sur téléphone.
  // Le reste du texte arrive juste après lui (cbl-entree, cbl-effets.css).
  if (banniere) {
    banniere.querySelectorAll('.cbl-hero__lien').forEach((lien) => {
      lien.classList.add('cbl-a-reveler', 'cbl-trace');
      lien.style.setProperty('--cbl-delai', '1300ms');
      deuxImages(() => lien.classList.add('cbl-revele'));
    });
  }

  /* ---------- Effets qui suivent le défilement ---------- */

  const actifs = new Map(); // élément → fonction qui le peint

  // Défilement lissé à la souris. La molette avance par crans d'une centaine de
  // pixels : sans lissage, les effets sautaient d'un coup (« saccadé » sur
  // ordinateur). Ici la position utilisée rattrape la vraie de 14 % par image.
  // Au doigt, le défilement du téléphone est déjà fluide : aucun lissage, aucun
  // retard.
  // Lissage désactivé : une fois le bon conteneur suivi, le défilement de Savor
  // est déjà doux (scroll-behavior: smooth) et chaque image est recalculée.
  // Le garder ajoutait un léger retard. Mettre `true` pour le réactiver.
  const lisser = false;
  let lisse = posY();
  let attendu = false;
  const peindre = () => {
    attendu = false;
    const vrai = posY();
    lisse = lisser ? lisse + (vrai - lisse) * 0.14 : vrai;
    if (Math.abs(vrai - lisse) < 0.5) lisse = vrai;
    // Les effets se calculent comme si la page était à `lisse` : un élément
    // paraît `decalage` pixels plus bas qu'il ne l'est.
    const decalage = vrai - lisse;
    actifs.forEach((fn) => fn(decalage));
    if (lisse !== vrai && actifs.size) {
      attendu = true;
      requestAnimationFrame(peindre);
    }
  };
  const planifier = () => {
    if (attendu || !actifs.size) return;
    attendu = true;
    requestAnimationFrame(peindre);
  };
  const proches = new IntersectionObserver((entrees) => {
    entrees.forEach((e) => {
      if (e.isIntersecting) actifs.set(e.target, e.target._cblPeindre);
      else actifs.delete(e.target);
    });
    planifier();
  }, { rootMargin: '40% 0px' });
  const suivre = (el, fn) => {
    el._cblPeindre = fn;
    proches.observe(el);
  };

  const borne = (v) => Math.min(1, Math.max(0, v));
  const doux = (v) => v * v * (3 - 2 * v); // départ et arrivée en douceur

  // 7. Bannière qui s'en va. Le texte reste plein tant que le titre n'a pas
  // atteint le milieu de l'écran, puis s'efface lentement : deux fois la course
  // qu'il faut au titre pour aller du milieu au haut de l'écran (au moins 60 %
  // d'un écran, sinon sur ordinateur, où le titre est déjà haut, il disparaissait
  // d'un coup).
  const panneau = banniere && banniere.querySelector('.cbl-hero__panneau');
  let titreDepart = 0;
  const mesurerBanniere = () => {
    if (!panneau || !titreBanniere) return;
    const ancien = panneau.style.translate;
    panneau.style.translate = '';
    titreDepart = titreBanniere.getBoundingClientRect().top + posY();
    panneau.style.translate = ancien;
  };
  if (panneau) {
    mesurerBanniere();
    suivre(panneau, () => {
      const s0 = Math.max(0, titreDepart - hauteur * 0.5); // titre au milieu
      const s1 = titreDepart - hauteur * 0.12; // titre en haut
      const etendue = Math.max(2 * (s1 - s0), hauteur * 0.6);
      const p = borne((lisse - s0) / etendue);
      const monte = borne(lisse / (hauteur * 0.8));
      if (!calme) panneau.style.translate = `0 ${(-60 * monte).toFixed(1)}px`;
      panneau.style.opacity = (1 - doux(p)).toFixed(3);
    });
  }

  // 6. Zoom arrière : 1,12 quand la photo entre par le bas, 1 quand elle sort par le haut.
  const suivreZooms = (racine) =>
    !calme && racine.querySelectorAll(ZOOMS).forEach((el) => {
      if (el.dataset.cblZoomPret) return;
      el.dataset.cblZoomPret = '1';
      suivre(el, (decalage) => {
        const r = el.getBoundingClientRect();
        const t = borne((hauteur - (r.top + decalage)) / (hauteur + r.height));
        const img = el.querySelector('img');
        if (img) img.style.scale = (1.12 - 0.12 * t).toFixed(4);
      });
    });

  // 9. Le texte s'efface en montant (scroll-linked fade out). Le bloc de texte
  // d'une section (surtitre, titre, paragraphe) reste plein tant que son bas est
  // sous 50 % de l'écran, puis devient très graduellement transparent : il n'a
  // complètement disparu qu'une fois sorti de l'écran (bas à -20 %). Il ne
  // s'efface donc jamais pendant qu'on le lit. En redescendant, il revient.
  // ATTENTION : balayer() l'appelle. S'il disparaît, le script plante au
  // démarrage et plus rien ne s'anime après la bannière (arrivé deux fois).
  const suivreSortie = (bloc) => {
    if (!bloc || bloc.dataset.cblSortie || bloc.closest('.cbl-hero')) return;
    bloc.dataset.cblSortie = '1';
    suivre(bloc, (decalage) => {
      const bas = (bloc.getBoundingClientRect().bottom + decalage) / hauteur;
      // Ordinateur : le bloc de texte est plus court par rapport à l'écran ; il
      // s'effaçait alors que le titre était encore au milieu. Il reste plein
      // jusqu'à 30 % de l'écran et ne disparaît qu'une fois sorti (-35 %).
      const o = innerWidth >= 990 ? doux(borne((bas + 0.35) / 0.65)) : doux(borne((bas + 0.2) / 0.7));
      bloc.style.opacity = o > 0.999 ? '' : o.toFixed(3);
    });
  };

  // 8. Fondu enchaîné entre sections — fondu croisé glissant (sliding cross-fade).
  // Chaque frontière porte deux dégradés complémentaires, un de chaque côté :
  //  - en haut de la section du bas, la couleur de la section du haut ;
  //  - en bas de la section du haut, la couleur de la section du bas.
  // Ils suivent la même courbe, inversée : à la ligne de coupure, les deux côtés
  // donnent toujours exactement la même couleur. La ligne ne se voit jamais.
  // Le point de fondu glisse : quand la frontière entre par le bas, il est loin
  // dans la section du bas (elle arrive de la couleur de celle du haut) ; il
  // remonte lentement, puis de plus en plus vite, traverse la frontière et finit
  // dans la section du haut, qui prend la couleur de la suivante juste avant de
  // sortir de l'écran. Tout passe par `translate` : la carte graphique s'en charge.
  const ENCRE = 'rgb(30, 27, 25)';
  const fondDe = (section) => {
    // La bannière (photo assombrie en bas) et l'érable (image de fond, bords
    // fondus vers l'encre, voir cbl-raffinements.css) se raccordent par l'encre.
    // Bas de la bannière : dégradé rgb(18 15 14 / 0.97) sur la photo.
    if (section.querySelector('.cbl-hero')) return 'rgb(19, 16, 15)';
    if (section.querySelector('.cbl-erable')) return ENCRE;
    const candidats = [section, ...section.querySelectorAll(':scope > *, :scope > * > *')];
    for (const el of candidats) {
      const c = getComputedStyle(el).backgroundColor;
      if (c && c !== 'transparent' && !/,\s*0\)$/.test(c)) return c;
    }
    return getComputedStyle(document.body).backgroundColor;
  };
  // « rgb(30, 27, 25) » → « 30 27 25 », pour composer le dégradé en CSS.
  const canaux = (couleur) => (couleur.match(/[\d.]+/g) || ['30', '27', '25']).slice(0, 3).join(' ');
  // Position du point de fondu à l'écran (0 = haut, 1 = bas) selon l'avancée p
  // de la frontière (0 = elle entre par le bas, 1 = au plus haut qu'elle ira).
  // Courbe au carré : lente au début, de plus en plus vite.
  // Calé pour des hauts de section de 8,5 à 10,5 rem sur téléphone : quand un
  // titre atteint 60 % de l'écran (moment où il se déplie), le fondu est déjà
  // passé au-dessus de lui. Plus rapide qu'avant (course 1,4 au lieu de 1,25,
  // exposant 1,6 au lieu de 2) : moins de zone « vide » entre deux sections.
  const pointDeFondu = (p) => 1.05 - 1.4 * Math.pow(p, 1.6);
  if (document.body.classList.contains('cbl-page-accueil')) {
    const sections = [...document.querySelectorAll('#MainContent > .shopify-section')];
    const couleurs = sections.map(fondDe);
    // Frontières sans fondu : une coupure nette au-dessus de ces sections. Elles
    // commencent par une photo, sans texte avant : le dégradé sur la photo n'est
    // pas beau (choix de Mavrick). Le fondu est réservé aux sections où du texte
    // arrive après le dégradé. Ici : « Cuit en petits lots » (après l'érable) et
    // le gâteau (après « Les plus demandés »).
    const SANS_FONDU = '.cbl-rayon, .cbl-gateau';
    const coupure = (i) => i > 0 && i < sections.length && !!sections[i].querySelector(SANS_FONDU);
    sections.forEach((s, i) => {
      // Deux cadres : celui de l'arrivée déborde de 3 px au-dessus de la section
      // (la frontière tombe souvent entre deux pixels : sans débord, une fine
      // ligne de la vraie couleur restait visible) ; celui du départ passe
      // au-dessus (z-index 5) et recouvre ce débord avec le bon mélange.
      const cadre = (sens) => {
        const c = document.createElement('div');
        c.className = `cbl-voiles cbl-voiles--${sens}`;
        c.setAttribute('aria-hidden', 'true');
        return c;
      };
      const voile = (sens, couleur) => {
        const c = cadre(sens);
        const v = document.createElement('div');
        v.className = `cbl-voile cbl-voile--${sens}`;
        v.style.setProperty('--cbl-voile-rgb', canaux(couleur));
        c.append(v);
        s.append(c);
        return v;
      };
      const arrivee = i > 0 && !coupure(i) ? voile('arrivee', couleurs[i - 1]) : null;
      const depart = i < sections.length - 1 && !coupure(i + 1) ? voile('depart', couleurs[i + 1]) : null;
      if (!arrivee && !depart) return;
      s.classList.add('cbl-a-voiles');
      // Décalage (px) entre le point de fondu et la frontière, pour une frontière
      // à la position `bord` (px depuis le haut de l'écran, avec le décalage).
      const ecart = (bord) => {
        const y = bord / hauteur;
        const plusHaut = (bord + posY() - (hautTotal() - hauteur)) / hauteur;
        const fin = Math.min(Math.max(0, plusHaut), 0.85);
        const p = borne((1 - y) / (1 - fin));
        // Tant que la frontière est encore sous l'écran (y > 1), le point de fondu
        // la suit à distance fixe, hors de vue. Sans ce min(), il restait collé au
        // bas de l'écran : un voile beige permanent en bas pendant qu'on lisait.
        return (pointDeFondu(p) - Math.min(y, 1)) * hauteur;
      };
      suivre(s, (decalage) => {
        const r = s.getBoundingClientRect();
        const largeurFondu = hauteur * 0.28;
        s.style.setProperty('--cbl-fondu', `${largeurFondu.toFixed(0)}px`);
        s.style.setProperty('--cbl-ecran', `${hauteur}px`);
        if (arrivee) {
          // Le bas du voile d'arrivée (fin du dégradé) se place à d + w/2 sous la
          // frontière ; le voile mesure 1,6 écran, plein au-dessus.
          const d = ecart(r.top + decalage);
          const ty = d + largeurFondu / 2 - hauteur * 1.6 + 3; // + 3 : débord du cadre d'arrivée
          arrivee.style.translate = `0 ${ty.toFixed(1)}px`;
        }
        if (depart) {
          // Le haut du voile de départ (début du dégradé) se place à d - w/2 de
          // la frontière du bas.
          const d = ecart(r.bottom + decalage);
          const ty = r.height + d - largeurFondu / 2;
          depart.style.translate = `0 ${ty.toFixed(1)}px`;
        }
      });
    });
  }

  addEventListener('scroll', planifier, { passive: true });
  if (enveloppe) enveloppe.addEventListener('scroll', planifier, { passive: true });
  addEventListener('resize', () => {
    // On ne remesure qu'à la rotation ou à un vrai changement de taille : la
    // barre d'adresse de Safari fait varier la hauteur d'environ 80 px.
    const h = racineHtml.clientHeight;
    if (innerWidth !== largeur || Math.abs(h - hauteur) > 120) {
      hauteur = h;
      largeur = innerWidth;
      mesurerBanniere();
    }
    planifier();
  }, { passive: true });

  /* ---------- démarrage ---------- */

  const demarrer = () => {
    balayer(document, true);
    suivreZooms(document);
    planifier();
    // Suggestions de produits, pages suivantes, menus rechargés : regroupés,
    // un seul balayage par image.
    const aRevoir = new Set();
    let prevu = false;
    new MutationObserver((mutations) => {
      for (const m of mutations)
        for (const n of m.addedNodes)
          if (n.nodeType === 1 && n.parentElement && !n.matches('.cbl-mot, .cbl-l, .cbl-lecteur, .cbl-voile'))
            aRevoir.add(n.parentElement);
      if (prevu || !aRevoir.size) return;
      prevu = true;
      requestAnimationFrame(() => {
        prevu = false;
        aRevoir.forEach((r) => r.isConnected && balayer(r, false));
        aRevoir.clear();
        suivreZooms(document);
      });
    }).observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') addEventListener('DOMContentLoaded', demarrer);
  else demarrer();
})();
